/* eslint-disable */

import { useState, useEffect, useRef } from 'react'
import { format, subMonths } from 'date-fns'
import { CalendarIcon, Clock, Download, FileText, RefreshCw, Search, Upload } from 'lucide-react'

import { Button } from '../../Components/ui/button'
import { Calendar } from '../../Components/ui/calendar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '../../Components/ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../Components/ui/table'
import { Badge } from '../../Components/ui/badge'
import { cn } from '../../lib/utils'
import { useRecoilValueLoadable } from 'recoil'
import { FetchChequeOrders } from '../../store/AdminStore'
import { formatIndianCurrency } from '../../utils/CurrencyUtil'
import { Input } from '../../Components/ui/input'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../Components/ui/accordian'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../Components/ui/dialog'
import { Label } from '../../Components/ui/label'
import { post } from '../../http/restservice'
import { CalendarDatePicker } from '../../Components/ui/calendar-date-picker'

const getStatusBadge = (isChequeOrder, chequeS3Key) => {
  let status
  let colorClass
  let icon

  if (isChequeOrder && chequeS3Key) {
    icon = <FileText className='w-3 h-3 mr-1' />

    status = 'Cheque Uploaded'
    colorClass = 'bg-green-100 text-green-800 hover:bg-green-100'
  } else {
    icon = <Clock className='w-3 h-3 mr-1' />

    status = 'Cheque Pending'
    colorClass = 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100'
  }

  return (
    <Badge className={colorClass}>
      {icon}
      {status}
    </Badge>
  )
}

const GroupedOrders = ({ groupedOrders }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedOrderGroup, setSelectedOrderGroup] = useState(null)

  // States for modal and upload
  const [clientCode, setClientCode] = useState(null)
  const [customerBasketInvestmentId, setCustomerBasketInvestmentId] = useState(null)
  const [currentOrderForUpload, setCurrentOrderForUpload] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadMessage, setUploadMessage] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const fileInputRef = useRef(null) // Ref for the file input in the modal

  const handleFileChange = (event) => {
    setUploadMessage(null)
    setUploadError(null)
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0]
      if (file.type === 'application/pdf') {
        setSelectedFile(file)
      } else {
        setSelectedFile(null)
        setUploadError('Please select a PDF file.')
      }
    } else {
      setSelectedFile(null)
    }
  }

  const handleUploadSubmit = async () => {
    if (!selectedFile || !currentOrderForUpload) {
      setUploadError('Please select a file and ensure an order is selected for upload.')
      return
    }

    setUploading(true)
    setUploadMessage(null)
    setUploadError(null)

    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('clientCode', clientCode)
    formData.append('orderNos', currentOrderForUpload)
    formData.append('customerBasketInvestmentId', customerBasketInvestmentId)

    try {
      const response = await post(
        'https://apiuat-mywealth.fabits.com/mutualfundservice/api/mutualFund/uploadChallanForChequeOrders',
        formData,
      )

      if (response?.status === 'SUCCESS' && response?.uploadedKey) {
        setUploadMessage(
          `File "${
            selectedFile.name
          }" for Order ${currentOrderForUpload} uploaded successfully! Response: ${JSON.stringify(
            response,
          )}`,
        )

        setSelectedFile(null) // Clear selected file after successful upload
        if (fileInputRef.current) {
          fileInputRef.current.value = '' // Reset file input
        }

        setTimeout(() => {
          setIsModalOpen(false)
          setUploadMessage(null)
        }, 2000)
        // Close modal on success
      }

      if (response?.isError) {
        setSelectedFile(null) // Clear selected file after successful upload
        if (fileInputRef.current) {
          fileInputRef.current.value = '' // Reset file input
        }
        setIsModalOpen(false) // Close modal on success
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }
    } catch (err) {
      setUploadError(
        `Failed to upload file for Order ${currentOrderForUpload.orderNumber}: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      )
    } finally {
      setUploading(false)
    }
  }

  const openUploadModal = (group) => {
    setSelectedOrderGroup(group)
    setIsModalOpen(true)
  }

  return (
    <>
      <Accordion type='multiple' className='w-full space-y-2'>
        {groupedOrders.map((group, groupIndex) => (
          <AccordionItem key={groupIndex} value={`group-${groupIndex}`}>
            <AccordionTrigger>
              <div className='flex flex-col text-left space-y-2'>
                <div className='flex space-x-4 items-center'>
                  <span className='font-medium'>
                    {group[0].clientCode} - {group.length} order{`${group.length > 1 ? 's' : ''}`}
                  </span>

                  <span className='font-medium'>
                    {getStatusBadge(group[0].isChequeOrder, group[0].chequeS3Key)}
                  </span>
                  {group[0].isChequeOrder && !group[0].chequeS3Key && (
                    <Button
                      variant='simple'
                      size='sm'
                      className='ml-2'
                      onClick={(e) => {
                        e.stopPropagation()
                        openUploadModal(group)
                        setClientCode(group[0]?.clientCode)
                        setCustomerBasketInvestmentId(group[0]?.customerBasketInvestmentId)
                        setCurrentOrderForUpload(
                          group?.map((order) => order.orderNumber)?.join(','),
                        )
                      }}
                    >
                      <Upload className='w-4 h-4' />
                    </Button>
                  )}
                </div>

                <p className='space-x-4'>
                  <span className='text-sm text-muted-foreground'>
                    Phone Number: {group[0].phoneNumber}
                  </span>
                  <span className='text-sm text-muted-foreground'>
                    CustomerBasketInvestmentID :{' '}
                    {group[0].customerBasketInvestmentId
                      ? group[0].customerBasketInvestmentId
                      : JSON.stringify(group[0].customerBasketInvestmentId)}
                  </span>
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className='space-y-3 pt-2'>
              {/* {group.map((order, orderIndex) => (
              <div key={orderIndex} className='text-sm rounded border p-3'>
                <p>
                  <strong>⏱️ Created:</strong> {new Date(order.createdTimeStamp).toLocaleString()}
                </p>
                <p>
                  <strong>🧺 Basket ID:</strong> {order.customerBasketInvestmentId}
                </p>
                <p>
                  <strong>💰 Amount:</strong> ₹{order.amount}
                </p>

                <span className='text-sm text-muted-foreground'>
                  {getStatusBadge(order.isChequeOrder, order.chequeS3Key)}
                </span>

              </div>
            ))} */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order Number</TableHead>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Scheme Code</TableHead>
                    <TableHead>Buy/Sell Type</TableHead>
                    <TableHead className='text-right'>Amount</TableHead>
                    <TableHead>Client Code</TableHead>
                    <TableHead>Created Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className='font-medium'>{order.orderNumber}</TableCell>
                      <TableCell className='font-semibold'>{order.phoneNumber}</TableCell>
                      <TableCell>{order.schemeCode}</TableCell>
                      <TableCell>
                        <Badge variant='outline'>
                          {order.buySellType}
                          {/* {order.buySell} */}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-right font-medium'>
                        ₹ {formatIndianCurrency(order.amount)}
                      </TableCell>
                      <TableCell className='text-right font-semibold'>
                        {order.clientCode}{' '}
                      </TableCell>
                      <TableCell>{format(new Date(order.createdTimeStamp), 'PPP')}</TableCell>
                      <TableCell>
                        {getStatusBadge(order.isChequeOrder, order.chequeS3Key)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Upload Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Upload Cheque</DialogTitle>
            <DialogDescription>
              Upload the cheque document for client - {selectedOrderGroup?.[0]?.clientCode}
            </DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 py-4'>
            <div className='grid gap-2'>
              <Label htmlFor='cheque-file'>Select Cheque File</Label>
              <Input
                id='cheque-file'
                type='file'
                accept='.pdf'
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </div>
            {selectedFile && (
              <p className='text-sm text-muted-foreground mt-1'>Selected: {selectedFile.name}</p>
            )}
            {uploadError && <p className='text-sm text-red-600 mt-1'>{uploadError}</p>}
            {uploadMessage && <p className='text-sm text-green-600 mt-1'>{uploadMessage}</p>}
          </div>
          <div className='flex justify-end space-x-2'>
            <Button variant='outline' onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUploadSubmit} disabled={!selectedFile}>
              Upload
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const ChequeOrders = () => {
  const [selectedDateRange, setSelectedDateRange] = useState({
    from: new Date(new Date().getFullYear(), 0, 1),
    to: new Date(),
  })
  const [searchQuery, setSearchQuery] = useState('')
  const fromDateStr = format(selectedDateRange.from, 'yyyy-MM-dd')
  const toDateStr = format(selectedDateRange.to, 'yyyy-MM-dd')
  const { state, contents: orders } = useRecoilValueLoadable(
    FetchChequeOrders({ fromDateStr, toDateStr }),
  )

  const filteredOrders =
    searchQuery && state === 'hasValue'
      ? orders?.data?.filter(
          (order) =>
            order.clientCode?.includes(searchQuery) || order.phoneNumber?.includes(searchQuery),
        )
      : orders?.data

  const exportToCSV = () => {
    const headers = [
      'Order Number',
      'Phone Number',
      'Scheme Code',
      'Buy/Sell Type',
      'Amount',
      'Client Code',
      'Created Date',
      'Status',
    ]
    const csvContent = [
      headers.join(','),
      ...orders?.data?.map((order) =>
        [
          order.orderNumber,
          order.phoneNumber,
          order.schemeCode,
          `${order.buySellType} ${order.buySell}`,
          order.amount,
          order.clientCode,
          order.createdTimeStamp,
          order.successFlag === '1' ? 'Success' : order.chequeS3Key ? 'Cheque Uploaded' : 'Pending',
        ].join(','),
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cheque-orders-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  function parseDate(ts) {
    return new Date(ts.replace(' ', 'T'))
  }

  function groupOrders(orders) {
    const groups = []

    const sortedOrders = [...orders]?.sort(
      (a, b) => parseDate(a.createdTimeStamp) - parseDate(b.createdTimeStamp),
    )

    for (let order of sortedOrders) {
      const createdAt = parseDate(order.createdTimeStamp)

      const foundGroup = groups.find((group) => {
        const groupCreatedAt = parseDate(group[0].createdTimeStamp)
        const timeDiff = (createdAt - groupCreatedAt) / (1000 * 60) // minutes

        return (
          group[0].customerBasketInvestmentId === order.customerBasketInvestmentId &&
          group[0].clientCode === order.clientCode &&
          timeDiff <= 2
        )
      })

      if (foundGroup) {
        foundGroup.push(order)
      } else {
        groups.push([order])
      }
    }

    return groups
  }

  return (
    <div className='container mx-auto p-6 space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>Cheque Orders</h1>
          <p className='text-muted-foreground'>
            Manage and track all cheque orders for mutual fund investments
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter Orders</CardTitle>
          <CardDescription>
            Select date range to fetch cheque orders. Default shows last 1 month data.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex flex-col sm:flex-row gap-4 items-end'>
            <div className='grid gap-2'>
              <label className='text-sm font-medium'>Select Date Range</label>
              <CalendarDatePicker date={selectedDateRange} onDateSelect={setSelectedDateRange} />

              {/* <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className={cn(
                      'w-[240px] justify-start text-left font-normal',
                      !fromDate && 'text-muted-foreground',
                    )}
                  >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    {fromDate ? format(fromDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' align='start'>
                  <Calendar
                    mode='single'
                    selected={fromDate}
                    onSelect={setFromDate}
                    initialFocus
                    showOutsideDays={true}
                    captionLayout='dropdown-buttons'
                    fromYear={2020}
                    toYear={2030}
                  />
                </PopoverContent>
              </Popover> */}
            </div>

            {/* <div className='grid gap-2'>
              <label className='text-sm font-medium'>To Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className={cn(
                      'w-[240px] justify-start text-left font-normal',
                      !toDate && 'text-muted-foreground',
                    )}
                  >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    {toDate ? format(toDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' align='start'>
                  <Calendar
                    mode='single'
                    selected={toDate}
                    onSelect={setToDate}
                    initialFocus
                    showOutsideDays={true}
                    captionLayout='dropdown-buttons'
                    fromYear={2020}
                    toYear={2030}
                  />
                </PopoverContent>
              </Popover>
            </div> */}

            <div className='flex gap-2'>
              <Button
                // onClick={fetchChequeOrders}
                disabled={!selectedDateRange.from || !selectedDateRange.to}
                className='gap-2'
              >
                {state === 'loading' ? (
                  <RefreshCw className='h-4 w-4 animate-spin' />
                ) : (
                  <Search className='h-4 w-4' />
                )}
                {state === 'loading' ? 'Fetching...' : 'Fetch Orders'}
              </Button>

              <Button
                variant='outline'
                onClick={exportToCSV}
                disabled={orders.length === 0}
                className='gap-2'
              >
                <Download className='h-4 w-4' />
                Export CSV
              </Button>
            </div>
          </div>

          <div className='flex flex-col sm:flex-row gap-4 items-end'>
            {/* New Client Code Filter */}
            {orders?.data?.length && (
              <div className='grid gap-2'>
                <label htmlFor='clientCode' className='text-sm font-medium'>
                  Client Code || Phone Number
                </label>
                <Input
                  id='clientCode'
                  placeholder='Enter client code'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='w-[280px]'
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {state === 'hasError' && (
        <Card className='border-destructive'>
          <CardContent className='pt-6'>
            <div className='flex items-center gap-2 text-destructive'>
              <span className='font-medium'>Error:</span>
              <span>Something went wrong, please try again later</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Orders ({filteredOrders?.length})</CardTitle>
          <CardDescription>
            {selectedDateRange.from && selectedDateRange.to && (
              <>
                Showing orders from {format(selectedDateRange.from, 'PPP')} to{' '}
                {format(selectedDateRange.to, 'PPP')}
              </>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {state === 'loading' ? (
            <div className='flex items-center justify-center py-8'>
              <RefreshCw className='h-8 w-8 animate-spin text-muted-foreground' />
              <span className='ml-2 text-muted-foreground'>Loading orders...</span>
            </div>
          ) : state === 'hasValue' &&
            orders?.message === 'No Cheque orders found for the specified user.' ? (
            <div className='text-center py-8 text-muted-foreground'>{orders?.message} </div>
          ) : (
            <div>
              <GroupedOrders groupedOrders={groupOrders(filteredOrders)} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default ChequeOrders
